import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import ManufacturersTable from './ManufacturersTable';
import ManufacturersTableMobile from './ManufacturersTableMobile';
import FilterManufacturersModal from './FilterManufacturersModal';
import ManufacturerModal from './ManufacturerModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchManufacturers,
  updateManufacturer,
  deleteManufacturer,
  addManufacturer,
} from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './manufacturers.scss';

const ManufacturersTableContainer = (props) => {
  const {
    manufacturers = [],
    distributors,
    createManufacturer,
    updateManufacturer,
    deleteManufacturer,
    fetchManufacturers,
    withButton,
    toggleSidebar,
    countries,
    users,
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ countries: [] });

  const onEditClick = (manufacturer) => {
    setSelectedManufacturer(manufacturer);
    toggleUpdateModal(true);
  };

  const onManufacturerCreate = async (formObj) => {
    try {
      setLoading(true);

      await createManufacturer(formObj);
      await fetchManufacturers();
      setSuccessMessage('New manufacturer successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onManufacturerUpdate = async (formObj) => {
    if (selectedManufacturer) {
      try {
        setLoading(true);

        await updateManufacturer(selectedManufacturer?.id, formObj);
        await fetchManufacturers();
        setSuccessMessage('Manufacturer successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteManufacturer = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteManufacturer(selectedId);
        await fetchManufacturers();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      distributors: filters.distributors || [],
      roles: filters.roles || [],
      countries: filters.countries || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredManufacturers = useMemo(() => {
    let filtered = manufacturers;

    if (search) {
      const q = search.toLowerCase();
      filtered = manufacturers.filter((m) => m?.name?.toLowerCase()?.includes(q));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((m) => filterState?.countries?.includes(m.country));
    }

    return filtered;
  }, [manufacturers, search, filterState]);

  const list = useMemo(() => {
    let result = filteredManufacturers;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredManufacturers, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Manufacturers
                  <Box className="badge">{manufacturers.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add and manage manufacturers
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text={isMobile ? 'Add' : 'Add Manufacturer'}
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {manufacturers.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{value}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }
              <ManufacturersTable
                manufacturers={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredManufacturers?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <ManufacturersTableMobile manufacturers={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Manufacturers</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteManufacturer}
        title="Delete Manufacturer"
        text="Are you sure you want to delete this manufacturer?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <ManufacturerModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onManufacturerCreate}
        countries={countries}
        loading={loading}
        usersList={users}
      />

      <ManufacturerModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedManufacturer(undefined);
        }}
        onSubmit={onManufacturerUpdate}
        countries={countries}
        mode='update'
        initialValues={selectedManufacturer}
        loading={loading}
        usersList={users}
      />

      <FilterManufacturersModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteManufacturer}
        title="Delete Manufacturer?"
        text="Are you sure you want to delete this manufacturer? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

ManufacturersTableContainer.propTypes = {
  manufacturers: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchManufacturers: () => dispatch(fetchManufacturers()),
    createManufacturer: (manufacturerData) => dispatch(addManufacturer(manufacturerData)),
    updateManufacturer: (id, manufacturerData) => dispatch(updateManufacturer(id, manufacturerData)),
    deleteManufacturer: (id) => dispatch(deleteManufacturer(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ManufacturersTableContainer);
